import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import AIReadinessAssessment from './components/AIReadinessAssessment';
import { trackServiceEngagement, trackCTAClick } from '../../utils/analyticsEvents';

const AISolutionsPage = () => {
  const navigate = useNavigate();
  const [expandedSolution, setExpandedSolution] = useState(null);

  const handleSolutionExpand = (solutionId) => {
    setExpandedSolution(expandedSolution === solutionId ? null : solutionId);
    if (expandedSolution !== solutionId) {
      trackServiceEngagement(`AI Solution: ${solutionId}`, 'expand');
    }
  };

  const handleCTAClick = (ctaName) => {
    trackCTAClick(ctaName, 'AI Solutions');
  };

  const coreSolutions = [
    {
      id: 'transparency',
      title: 'AI Transparency & Explainability',
      subtitle: 'Building trust in automated decision-making',
      icon: 'Shield',
      color: 'from-blue-500 to-cyan-500',
      whatWeHelp: [
        'Explainable AI frameworks for business and regulatory audiences',
        'Decision traceability and audit readiness',
        'Governance models for automated and semi-automated decisions',
        'Risk classification of AI use cases (high / medium / low impact)'
      ],
      whyItMatters: [
        'Regulatory scrutiny under European AI legislation',
        'Board accountability for AI-driven outcomes',
        'Customer and employee trust in automated decisions'
      ],
      useCases: [
        'Credit and eligibility decisions',
        'Pricing and personalisation engines',
        'Fraud detection and risk scoring',
        'AI-assisted HR or customer service workflows'
      ]
    },
    {
      id: 'age-verification',
      title: 'Age Verification & Regulatory-Aligned AI Controls',
      subtitle: 'Compliant design for European digital services',
      icon: 'UserCheck',
      color: 'from-purple-500 to-pink-500',
      whatWeHelp: [
        'Age assurance strategy aligned to European legal requirements',
        'Privacy-preserving age verification approaches',
        'Risk-based access controls for AI-driven services',
        'Governance for AI systems interacting with minors'
      ],
      whyItMatters: [
        'Legal obligations around child protection and online safety',
        'High reputational risk for non-compliance',
        'Growing enforcement expectations across EU member states'
      ],
      outcome: 'A defensible, proportionate age-verification approach that protects users without damaging customer experience or breaching privacy principles.'
    },
    {
      id: 'recommendations',
      title: 'AI-Driven Recommendations & Personalisation',
      subtitle: 'Turning data into revenue growth',
      icon: 'Target',
      color: 'from-emerald-500 to-teal-500',
      whatWeHelp: [
        'Recommendation system strategy (products, content, offers)',
        'Governance for personalisation and profiling',
        'Bias and fairness assessment in recommendation engines',
        'Measuring commercial impact (uplift, conversion, retention)'
      ],
      businessImpact: [
        'Increased conversion rates',
        'Improved customer lifetime value',
        'Reduced churn',
        'Better targeting without over-personalisation risk'
      ]
    },
    {
      id: 'automation',
      title: 'Workflow Automation & AI-Augmented Operations',
      subtitle: 'Efficiency without loss of control',
      icon: 'Zap',
      color: 'from-orange-500 to-red-500',
      whatWeHelp: [
        'Identification of automation-ready workflows',
        'Human-in-the-loop AI design',
        'AI governance for operational decision automation',
        'Measuring efficiency gains and risk reduction'
      ],
      typicalAreas: [
        'Finance and invoicing',
        'Customer support triage',
        'Supply chain and logistics planning',
        'Internal reporting and decision support'
      ]
    },
    {
      id: 'governance',
      title: 'AI Governance & Operating Models',
      subtitle: 'Making AI scalable, safe, and sustainable',
      icon: 'Settings',
      color: 'from-indigo-500 to-purple-500',
      whatWeHelp: [
        'AI governance frameworks integrated with data governance',
        'Clear ownership and accountability models',
        'AI risk registers and approval workflows',
        'Operating models for AI at scale'
      ],
      outcome: 'AI becomes a managed capability — not a collection of disconnected experiments.'
    }
  ];

  const seminars = [
    {
      id: 'half-day',
      title: 'AI Readiness for Business Leaders',
      duration: '½-Day Executive Seminar',
      time: 'Approx. 3.5–4 hours',
      audience: ['Senior managers', 'Directors', 'Heads of function', 'Non-technical executives'],
      objective: 'Give leaders clarity on what AI can and should be used for, what risks they personally own, and where AI can realistically drive revenue or efficiency.',
      sessions: [
        {
          title: 'AI Reality Check',
          duration: '60 mins',
          topics: ['What AI is actually being used for today', 'Myths vs reality', 'Where AI typically fails in organisations'],
          outcome: 'Shared, realistic baseline'
        },
        {
          title: 'AI, Risk & Regulation',
          duration: '60 mins',
          topics: ['AI transparency and explainability (plain English)', 'Age verification and protected users', 'Accountability for automated decisions', 'What senior managers are personally responsible for'],
          outcome: 'Risk awareness without fear-mongering'
        },
        {
          title: 'Commercial Impact Workshop',
          duration: '90 mins',
          topics: ['Identifying revenue-generating AI use cases', 'Identifying efficiency-driven AI opportunities', 'Mapping AI use cases to measurable outcomes', 'Avoiding "AI theatre"'],
          outcome: 'A short, prioritised AI opportunity list'
        },
        {
          title: 'Readiness Summary',
          duration: '30 mins',
          topics: ['What the organisation can do immediately', 'What must come first', 'What to avoid'],
          outcome: 'Clear next steps'
        }
      ]
    },
    {
      id: 'full-day',
      title: 'Using AI to Drive Growth and Efficiency — Safely',
      duration: 'Full-Day Executive Seminar',
      time: 'Full day',
      audience: ['Leadership teams planning active AI adoption'],
      objective: 'Move from awareness → actionable direction.',
      sessions: [
        {
          title: 'AI in Business Today',
          duration: '90 mins',
          phase: 'Morning: Foundations & Risk',
          topics: ['AI capabilities that actually work', 'Case examples across sectors', 'Decision automation vs decision support']
        },
        {
          title: 'Trust, Transparency & Compliance',
          duration: '90 mins',
          phase: 'Morning: Foundations & Risk',
          topics: ['Explainable AI for executives', 'Age verification and European obligations', 'Governance patterns that work', 'Where AI failures typically occur']
        },
        {
          title: 'Revenue Growth with AI',
          duration: '90 mins',
          phase: 'Afternoon: Application & Strategy',
          topics: ['Personalisation and recommendations', 'Pricing and demand optimisation', 'Customer engagement automation', 'Measuring ROI realistically']
        },
        {
          title: 'Operational Efficiency with AI',
          duration: '90 mins',
          phase: 'Afternoon: Application & Strategy',
          topics: ['Workflow automation opportunities', 'AI-augmented decision making', 'Human-in-the-loop design', 'Efficiency vs control trade-offs']
        },
        {
          title: 'AI Readiness Roadmap',
          duration: '60 mins',
          phase: 'Final Session',
          topics: ['What to do in the next 90 days', 'What needs governance first', 'What requires executive sponsorship', 'How to avoid fragmented AI adoption']
        }
      ],
      outputs: [
        'A shared understanding of AI risk and opportunity',
        'A prioritised, defensible AI action plan',
        'Clear leadership responsibilities'
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>AI Solutions - Practical AI for Trust, Compliance & Business Impact | Northstar Analytics</title>
        <meta name="description" content="Deploy AI responsibly and commercially with transparency, regulatory alignment, and measurable value across revenue growth and operational efficiency." />
      </Helmet>

      <Header />

      <main className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <section className="pt-32 pb-20 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full mb-6">
              <Icon name="Sparkles" size={20} className="text-primary" />
              <span className="text-sm font-semibold text-primary">Responsible AI Implementation</span>
            </div>
            
            <h1 className="text-5xl lg:text-6xl font-bold text-primary mb-6">
              Practical AI Solutions Built for
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary mt-2">
                Trust, Compliance, and Business Impact
              </span>
            </h1>
            
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              We help organisations deploy AI responsibly and commercially — ensuring transparency, 
              regulatory alignment, and measurable value across revenue growth and operational efficiency.
            </p>

            <div className="max-w-4xl mx-auto text-left bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg mb-10">
              <p className="text-lg text-slate-700 leading-relaxed">
                Artificial Intelligence is now embedded in customer journeys, operational workflows, and decision-making processes. 
                For European organisations, this creates both opportunity and obligation. AI must be explainable, compliant, and 
                aligned to business outcomes — not experimental or opaque. Northstar Analytics works with leadership teams to 
                design and implement AI solutions that build trust, meet regulatory expectations, and deliver tangible commercial impact.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                onClick={() => document.getElementById('core-solutions')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-secondary hover:bg-secondary/90 hover-lift"
              >
                Explore AI Solutions
              </Button>
              <Button
                size="lg"
                variant="outline"
                iconName="BookOpen"
                iconPosition="left"
                onClick={() => document.getElementById('seminars')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Executive Seminars
              </Button>
            </div>
          </div>
        </section>

        {/* AI Readiness Assessment Section */}
        <section className="py-16 px-4 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-secondary/10 to-primary/10 rounded-full mb-4">
                <Icon name="Target" size={20} className="text-secondary" />
                <span className="text-sm font-semibold text-secondary">Free Assessment Tool</span>
              </div>
              <h2 className="text-4xl font-bold text-primary mb-4">Assess Your AI Readiness</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Evaluate your organization's AI maturity across data infrastructure, governance, team capabilities, 
                and strategic alignment. Get personalized recommendations for your AI journey.
              </p>
            </div>

            <AIReadinessAssessment />

            <div className="mt-8 text-center">
              <p className="text-sm text-muted-foreground">
                <Icon name="Lock" size={16} className="inline mr-1" />
                Your responses are confidential and can be saved to track progress over time
              </p>
            </div>
          </div>
        </section>

        <section id="core-solutions" className="py-16 px-4 bg-white/50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-primary mb-4">Core Solution Areas</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Comprehensive AI solutions designed for regulatory compliance and commercial effectiveness
              </p>
            </div>

            <div className="space-y-6">
              {coreSolutions?.map((solution, index) => (
                <div
                  key={solution?.id}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200 hover:shadow-xl transition-all duration-300"
                >
                  <div
                    className="p-6 cursor-pointer"
                    onClick={() => setExpandedSolution(expandedSolution === solution?.id ? null : solution?.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className={`p-3 rounded-xl bg-gradient-to-br ${solution?.color} text-white`}>
                          <Icon name={solution?.icon} size={28} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="text-sm font-bold text-primary/60">0{index + 1}</span>
                            <h3 className="text-2xl font-bold text-primary">{solution?.title}</h3>
                          </div>
                          <p className="text-slate-600 text-lg">{solution?.subtitle}</p>
                        </div>
                      </div>
                      <Icon
                        name={expandedSolution === solution?.id ? 'ChevronUp' : 'ChevronDown'}
                        size={24}
                        className="text-primary mt-2"
                      />
                    </div>
                  </div>

                  {expandedSolution === solution?.id && (
                    <div className="px-6 pb-6 pt-2 border-t border-slate-100">
                      <div className="grid md:grid-cols-2 gap-8 mt-6">
                        <div>
                          <h4 className="text-lg font-bold text-primary mb-3 flex items-center">
                            <Icon name="CheckCircle" size={20} className="mr-2 text-secondary" />
                            What we help with
                          </h4>
                          <ul className="space-y-2">
                            {solution?.whatWeHelp?.map((item, idx) => (
                              <li key={idx} className="flex items-start">
                                <span className="text-secondary mr-2 mt-1">•</span>
                                <span className="text-slate-700">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {solution?.whyItMatters && (
                          <div>
                            <h4 className="text-lg font-bold text-primary mb-3 flex items-center">
                              <Icon name="AlertCircle" size={20} className="mr-2 text-secondary" />
                              Why it matters
                            </h4>
                            <ul className="space-y-2">
                              {solution?.whyItMatters?.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="text-secondary mr-2 mt-1">•</span>
                                  <span className="text-slate-700">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {solution?.businessImpact && (
                          <div>
                            <h4 className="text-lg font-bold text-primary mb-3 flex items-center">
                              <Icon name="TrendingUp" size={20} className="mr-2 text-secondary" />
                              Business impact
                            </h4>
                            <ul className="space-y-2">
                              {solution?.businessImpact?.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="text-secondary mr-2 mt-1">•</span>
                                  <span className="text-slate-700">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {solution?.useCases && (
                          <div>
                            <h4 className="text-lg font-bold text-primary mb-3 flex items-center">
                              <Icon name="Briefcase" size={20} className="mr-2 text-secondary" />
                              Typical use cases
                            </h4>
                            <ul className="space-y-2">
                              {solution?.useCases?.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="text-secondary mr-2 mt-1">•</span>
                                  <span className="text-slate-700">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {solution?.typicalAreas && (
                          <div>
                            <h4 className="text-lg font-bold text-primary mb-3 flex items-center">
                              <Icon name="Layers" size={20} className="mr-2 text-secondary" />
                              Typical areas
                            </h4>
                            <ul className="space-y-2">
                              {solution?.typicalAreas?.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="text-secondary mr-2 mt-1">•</span>
                                  <span className="text-slate-700">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {solution?.outcome && (
                        <div className="mt-6 p-4 bg-gradient-to-r from-secondary/10 to-primary/10 rounded-xl border border-secondary/20">
                          <h4 className="text-sm font-bold text-primary mb-2 uppercase tracking-wide">Outcome</h4>
                          <p className="text-slate-700 font-medium">{solution?.outcome}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="seminars" className="py-16 px-4 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-primary mb-4">Executive Seminars: AI Readiness & Business Impact</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                These are not technical courses. They are decision-maker enablement sessions designed for senior leaders.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {seminars?.map((seminar) => (
                <div key={seminar?.id} className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
                  <div className="bg-gradient-to-r from-primary to-secondary p-6 text-white">
                    <div className="text-sm font-semibold mb-2 opacity-90">{seminar?.duration}</div>
                    <h3 className="text-2xl font-bold mb-2">{seminar?.title}</h3>
                    <div className="text-sm opacity-90">{seminar?.time}</div>
                  </div>

                  <div className="p-6">
                    <div className="mb-6">
                      <h4 className="text-sm font-bold text-primary mb-3 uppercase tracking-wide">Audience</h4>
                      <div className="flex flex-wrap gap-2">
                        {seminar?.audience?.map((aud, idx) => (
                          <span key={idx} className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">
                            {aud}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <h4 className="text-sm font-bold text-primary mb-2 uppercase tracking-wide">Objective</h4>
                      <p className="text-slate-700">{seminar?.objective}</p>
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-sm font-bold text-primary uppercase tracking-wide">Structure</h4>
                      {seminar?.sessions?.map((session, idx) => (
                        <div key={idx} className="border-l-4 border-secondary pl-4 py-2">
                          {session?.phase && (
                            <div className="text-xs font-bold text-secondary mb-1 uppercase">{session?.phase}</div>
                          )}
                          <div className="flex items-start justify-between mb-2">
                            <h5 className="font-bold text-primary">{session?.title}</h5>
                            <span className="text-sm text-slate-500 ml-2">{session?.duration}</span>
                          </div>
                          <ul className="text-sm text-slate-600 space-y-1 mb-2">
                            {session?.topics?.map((topic, topicIdx) => (
                              <li key={topicIdx} className="flex items-start">
                                <span className="text-secondary mr-2">•</span>
                                <span>{topic}</span>
                              </li>
                            ))}
                          </ul>
                          {session?.outcome && (
                            <div className="text-sm font-medium text-secondary italic">→ {session?.outcome}</div>
                          )}
                        </div>
                      ))}
                    </div>

                    {seminar?.outputs && (
                      <div className="mt-6 p-4 bg-gradient-to-r from-secondary/10 to-primary/10 rounded-xl">
                        <h4 className="text-sm font-bold text-primary mb-3 uppercase tracking-wide">Output for Participants</h4>
                        <ul className="space-y-2">
                          {seminar?.outputs?.map((output, idx) => (
                            <li key={idx} className="flex items-start">
                              <Icon name="CheckCircle" size={16} className="text-secondary mr-2 mt-1" />
                              <span className="text-slate-700 text-sm">{output}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-primary mb-4">Why This Works for Senior Managers</h3>
              <div className="grid md:grid-cols-5 gap-6">
                {[
                  { icon: 'Code', label: 'No coding', color: 'text-blue-500' },
                  { icon: 'ShieldCheck', label: 'No vendor bias', color: 'text-purple-500' },
                  { icon: 'TrendingDown', label: 'No hype', color: 'text-emerald-500' },
                  { icon: 'Users', label: 'Clear accountability', color: 'text-orange-500' },
                  { icon: 'DollarSign', label: 'Direct linkage to revenue and efficiency', color: 'text-pink-500' }
                ]?.map((item, idx) => (
                  <div key={idx} className="text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-3 ${item?.color}`}>
                      <Icon name={item?.icon} size={28} />
                    </div>
                    <p className="text-sm font-medium text-slate-700">{item?.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 px-4 bg-gradient-to-br from-primary to-primary/90 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Explore how AI can deliver value — without introducing unmanaged risk</h2>
            <p className="text-xl opacity-90 mb-8">
              Talk to us about designing AI solutions that are compliant, explainable, and commercially effective.
            </p>
            
            <Button
              size="lg"
              variant="outline"
              iconName="ArrowRight"
              iconPosition="right"
              onClick={() => navigate('/contact-multi-channel-engagement-hub')}
              className="bg-white text-primary hover:bg-white/90 border-white hover-lift"
            >
              Start the Conversation
            </Button>
          </div>
        </section>
      </main>
    </>
  );
};

export default AISolutionsPage;